import * as React from "react";
import NextImage from "next/image";
import { useRouter } from "next/router";
import {
  Text,
  Heading,
  Stack as ChakraStack,
  Box,
  Stack,
  Grid,
} from "@chakra-ui/react";

import Button from "components/Chakra/Button";
import CustomLink from "components/Chakra/CustomLink";
import { useAlertModal } from "modals/useAlertModal";
import ContactBadge from "components/ContactBadge";
import Logos from "components/Logos";
import Container from "components/Chakra/Container";
import MetaHead from "components/MetaHead";
import CoopForm from "./components/CoopForm";
import IconDownload from "components/Chakra/Icons/Download";
import useScrollToRef from "hooks/useScrollToRef";

const LOGOS = [
  {
    url: "https://www.bravis.cz/",
    src: "/img/common/logos/bravis.png",
    alt: "Logo BRAVIS",
  },
  {
    url: "https://www.century21.cz/",
    src: "/img/common/logos/century-21.png",
    alt: "Logo CENTURY21",
  },
  {
    url: "https://www.justo.cz/",
    src: "/img/common/logos/justo.png",
    alt: "Logo JUSTO",
  },
  {
    url: "https://www.mmreality.cz/",
    src: "/img/common/logos/mm-reality.png",
    alt: "Logo M&M REALITY",
  },
  {
    url: "https://qara.cz/",
    src: "/img/common/logos/qara.png",
    alt: "Logo QARA",
  },
  {
    url: "https://www.realspektrum.cz/",
    src: "/img/common/logos/real-spektrum.png",
    alt: "Logo REAL SPEKTRUM",
  },
  {
    url: "https://www.soccer-reality.cz/",
    src: "/img/common/logos/soccer.png",
    alt: "Logo Soccer REALITY",
  },
];

const SOFTS = [
  {
    url: "https://www.realitni-system.com/",
    src: "/img/common/softs/poskireal.png",
    alt: "Logo Poski Real",
  },
  {
    url: "https://irest.cz/",
    src: "/img/common/softs/irest.png",
    alt: "Logo iRest",
  },
  {
    url: "https://www.softreal.cz/",
    src: "/img/common/softs/softreal.png",
    alt: "Logo softreal",
  },
  {
    url: "https://www.realman.cz/inzerce/",
    src: "/img/common/softs/realman.png",
    alt: "Logo Realman",
  },
  {
    url: "https://www.realbrana.cz/",
    src: "/img/common/softs/realbrana.png",
    alt: "Logo REALBrána",
  },
  {
    url: "https://www.exportio.cz/",
    src: "/img/common/softs/exportio.png",
    alt: "Logo exportio",
  },
  {
    url: "https://www.realko.cz/",
    src: "/img/common/softs/realko.png",
    alt: "Logo REALKO",
  },
  {
    url: "https://realitni-spravce.cz/",
    src: "/img/common/softs/realitnispravce.png",
    alt: "Logo realitní-správce",
  },
  {
    url: "https://realitni-spravce.cz/",
    src: "/img/common/softs/reego.png",
    alt: "Logo Reego",
  },
  {
    url: "https://realitni-spravce.cz/",
    src: "/img/common/softs/eurobydleni.png",
    alt: "Logo eurobydleni.cz",
  },
  {
    url: "http://www.realitni-software.cz/",
    src: "/img/common/softs/estatix.png",
    alt: "Logo Estatix",
  },
  {
    url: "http://www.realitni-software.cz/",
    src: "/img/common/softs/reals.png",
    alt: "Logo Reals.cz",
  },
];

interface IRealEstateItem {
  icon: string;
  title: string;
  description: React.ReactNode;
}
const RealEstateItem: React.FC<IRealEstateItem> = ({
  icon,
  title,
  description,
}) => (
  <ChakraStack direction="row" spacing="10px">
    <ChakraStack minW="60px" minH="60px">
      <NextImage
        src={icon}
        alt={title}
        width="60"
        height="60"
        style={{ objectFit: "contain" }}
      />
    </ChakraStack>
    <ChakraStack>
      <Text size="large" fontWeight="bold" textAlign="left">
        {title}
      </Text>
      {description}
    </ChakraStack>
  </ChakraStack>
);

interface IStepItem {
  step: number;
  title: string;
  description: React.ReactNode;
}
const StepItem: React.FC<IStepItem> = ({ step, title, description }) => (
  <Stack justify="flex-start">
    <Stack spacing={0}>
      <Text textColor="secondary" size="small" mb={1}>
        {step}. krok
      </Text>
      <Text size="large" fontWeight="bold" mb={2} textAlign="left">
        {title}
      </Text>
    </Stack>
    {description}
  </Stack>
);

const RealEstateAgency: React.FC = () => {
  const { query } = useRouter();
  const { openModal: openAlertModal } = useAlertModal();

  const pricelist = query?.pricelist?.toString() === "cenik";

  const { ref, executeScroll } = useScrollToRef<HTMLDivElement>({
    behavior: "smooth",
    block: "center",
  });

  React.useEffect(() => {
    if (pricelist) {
      const top = document.getElementById("pricelist")?.offsetTop || 0;
      window.scrollTo({
        behavior: "smooth",
        top: top - 100,
      });
    }
  }, []);

  return (
    <>
      <MetaHead
        title="Výhodná inzerce pro realitní makléře"
        description="Získejte kvalitní zájemce pro své klienty. Specializujeme se na pronájmy bytů a v tomto ohledu jsme největší portál v České republice."
      />
      <Container>
        <Heading as="h1" mt="7rem" mb="2rem" data-test="rkform.heading">
          Získejte kvalitní zájemce pro své klienty
        </Heading>
        <Text textAlign="center" size="large" mb={15} data-test="rkform.text">
          Specializujeme se na pronájmy bytů a v tomto ohledu jsme největší
          portál v České republice.
        </Text>
        <Stack
          direction={{ base: "column", md: "row" }}
          flexWrap="wrap"
          justify="space-between"
          gap="28px"
          data-test="rkform.points"
        >
          <Stack alignItems="center" flex={1}>
            <Text textAlign="center" size="large" mb={3}>
              <b>až 14 000</b>
              <br />
              zájemců u nás denně <br />
              hledá bydlení
            </Text>
            <NextImage
              priority
              src="/img/icons/arrow_up.svg"
              alt=""
              width="78"
              height="39"
            />
          </Stack>
          <Stack alignItems="center" flex={1}>
            <Text textAlign="center" size="large" mb={3}>
              <b>přes 40 000</b>
              <br />
              zájemců měsíčně
              <br />
              propojíme s inzerenty
            </Text>
            <NextImage
              priority
              src="/img/icons/arrow_up.svg"
              alt=""
              width="78"
              height="39"
            />
          </Stack>
          <Stack alignItems="center" flex={1}>
            <Text textAlign="center" size="large" mb={3}>
              <b>500 +</b>
              <br />
              realitních kanceláří
              <br />
              s námi inzeruje
            </Text>
            <NextImage
              priority
              src="/img/icons/arrow_up.svg"
              alt=""
              width="78"
              height="39"
            />
          </Stack>
        </Stack>
        <Stack alignItems="center" justify="center" my="60px">
          <Button onClick={executeScroll} dataTest="rkform.coworkButton">
            Mám zájem o spolupráci
          </Button>
        </Stack>
      </Container>
      <Logos priority brands={LOGOS} />
      <Container>
        <div id="pricelist" />
        <Heading
          as="h2"
          mt="6rem"
          mb="5rem"
          fontWeight="light"
          textAlign="center"
          data-test="rkform.tenantText"
        >
          Pomůžeme vám najít spolehlivé nájemníky
        </Heading>
        <Grid
          my="60px"
          gap="40px"
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
          data-test="rkform.tenantPoints"
        >
          <RealEstateItem
            icon="/img/icons/exekuce.svg"
            title="Profily zájemců"
            description={
              <Text textAlign="left">
                Díky{" "}
                <Button
                  variant="link"
                  onClick={() =>
                    openAlertModal({
                      children: (
                        <Stack spacing={0}>
                          <Stack
                            justify="center"
                            alignItems="center"
                            spacing={0}
                          >
                            <NextImage
                              src="/img/common/vizitka.jpg"
                              alt="Náhled vizitky"
                              width="294"
                              height="370"
                            />
                          </Stack>
                          <Text textAlign="center" mt={4}>
                            O zájemcích víme více. Dozvíte se, jestli pracují,
                            či studují, kolik jim je let, kdy se chtějí
                            stěhovat, jestli mají zvíře a jiné užitečné
                            informace.
                          </Text>
                        </Stack>
                      ),
                    })
                  }
                >
                  vizitkám zájemců
                </Button>{" "}
                přesně víte, kdy se mohou nastěhovat, na jakou dobu hledají
                bydlení nebo zda se jedná o rodinu či studenty.
              </Text>
            }
          />

          <RealEstateItem
            icon="/img/icons/pocitac.svg"
            title="Přehledná správa inzerce"
            description={
              <Text textAlign="left">
                Ve svém účtu máte <b>inzeráty a zprávy zájemců pohromadě</b>,
                včetně přehledných statistik o výkonnosti jednotlivých nabídek.
              </Text>
            }
          />

          <RealEstateItem
            icon="/img/icons/penize.svg"
            title="Platba za aktivní inzerát"
            description={
              <Text textAlign="left">
                Inzerci účtujeme denní sazbou dle množství aktivních inzerátů.
                Čím víc aktivních inzerátů máte, tím méně za každý z nich
                zaplatíte.
              </Text>
            }
          />

          <RealEstateItem
            icon="/img/icons/raketa.svg"
            title="Snadný import"
            description={
              <Text textAlign="left">
                Podporujeme většinu <b>realitních softwarů</b>. V případě
                vlastního softwaru je vám plně k dispozici importní můstek
                UlovDomov.cz.
              </Text>
            }
          />
        </Grid>

        <Logos brands={SOFTS} colorOnHover />

        <Stack spacing={8} my="120px" direction={{ base: "column", sm: "row" }}>
          <Stack flex={1}>
            <Heading
              as="h3"
              mb="0.5rem"
              textAlign="left"
              data-test="rkform.protectClientsHeading"
            >
              Chraňte své klienty před rizikem
            </Heading>
            <Text
              mb={2}
              textAlign="left"
              data-test="rkform.protectClientsText1"
            >
              Věděli jste, že v Česku má každý 10. alespoň jednu exekuci?
              Prověřením záznamů o zájemcích ušetříte klientům starosti
              s rizikovými nájemníky.
            </Text>
            <Text
              mb={2}
              textAlign="left"
              data-test="rkform.protectClientsText2"
            >
              Prohledáme za vás 5 českých i slovenských rejstříků, od evidence
              exekucí až po seznam osob hledaných policií. Díky prověření
              získáte kvalitní zájemce a budujete dobré jméno své kanceláře.
            </Text>
            <Stack spacing={3} pt="42px">
              <Stack spacing={0} direction="row">
                <CustomLink
                  to="/balicky-provereni"
                  trackEventPayload={{
                    "dl.eCat": "RK",
                    "dl.eAct": "cta_balicky_cc",
                    "dl.eLab": "pro_RK",
                  }}
                  variant="button_outline"
                  dataTest="rkform.creditCheckButton"
                >
                  Balíčky prověření
                </CustomLink>
              </Stack>
              <CustomLink
                href="/documents/ccreport.pdf"
                trackEventPayload={{
                  "dl.eCat": "RK",
                  "dl.eAct": "rk_materialy",
                  "dl.eLab": "vzor_cc",
                }}
                isExternal
                data-test="rkform.downloadPatternReport"
              >
                <Stack direction="row" alignItems="center" flexWrap="nowrap">
                  <IconDownload fillWith="ud-primary" />
                  <Text textColor="primary">Stáhnout vzor reportu</Text>
                </Stack>
              </CustomLink>
            </Stack>
          </Stack>
          <Stack alignItems="center" flex={1}>
            <CustomLink href="/documents/ccreport.pdf" isExternal>
              <NextImage
                src="/img/common/report_provereni.jpg"
                width="270"
                height="330"
                alt="Náhled reportu prověření"
              />
            </CustomLink>
          </Stack>
        </Stack>
        <Heading
          as="h2"
          mb="5rem"
          mt="6rem"
          textAlign="center"
          fontWeight="light"
          data-test="rkform.advertiseHeading"
        >
          Inzerujte s námi – první měsíc je gratis
        </Heading>
        <Stack
          mb="60px"
          gap="24px"
          direction={{ base: "column", sm: "row" }}
          data-test="rkform.advertiseItems"
        >
          <StepItem
            step={1}
            title="Měsíc na zkoušku"
            description={
              <Text>
                Spolupráci s naším inzertním portálem si můžete na{" "}
                <b>30 dní vyzkoušet zdarma</b>.
              </Text>
            }
          />
          <StepItem
            step={2}
            title="Placené období"
            description={
              <Text>
                V případě spokojenosti navážeme po zkušební době s placenou
                inzercí.
              </Text>
            }
          />
          <StepItem
            step={3}
            title="Vyúčtování"
            description={
              <Text>
                Kvartální faktura zahrnuje podrobnou statistiku k jednotlivým
                inzerátům.
              </Text>
            }
          />
        </Stack>

        <Box
          ref={ref}
          mt="60px"
          mb="90px"
          boxShadow="0 0 10px 0 rgba(0, 0, 0, 0.19)"
          p={["16px", null, "42px"]}
          borderRadius="6px"
        >
          <Stack spacing={0} alignItems="center">
            <Stack alignItems="center">
              <Heading as="h3" data-test="rkform.formHeading">
                Vyzkoušejte inzerci na měsíc zdarma
              </Heading>
              <Text pb={7} data-test="rkform.formText">
                Kontaktujte naše obchodní zástupce, rádi vám sestavíme nabídku
                na míru.
              </Text>
            </Stack>

            <Stack
              direction={{ base: "column", lg: "row" }}
              w="100%"
              spacing={{ base: "42px", lg: 0 }}
            >
              <Stack flex={1}>
                <CoopForm />
              </Stack>
              <Stack
                alignItems="center"
                justify="center"
                spacing={3}
                flex={1}
                data-test="rkform.contactBadge"
              >
                <Stack alignItems="flex-start" justify="center" spacing={3}>
                  <ContactBadge
                    imageUrl="/img/common/kolacek.jpg"
                    name="Petr Koláček"
                    title="Key Account Manager"
                    email="kolacek@ulovdomov.cz"
                    phone="+420 773 128 704"
                  />
                </Stack>
                <CustomLink
                  href="/documents/vyhodna_inzerce.pdf"
                  trackEventPayload={{
                    "dl.eCat": "RK",
                    "dl.eAct": "rk_materialy",
                    "dl.eLab": "prehled_o_spolupraci",
                  }}
                  isExternal
                  data-test="rkform.CoopOverview"
                >
                  <Stack
                    alignItems="center"
                    direction="row"
                    flexWrap="nowrap"
                    mt="30px"
                  >
                    <IconDownload id="download" fillWith="ud-primary" />
                    <Text variant="small_2" color="ud-primary">
                      Stáhnout Přehled o spolupráci
                    </Text>
                  </Stack>
                </CustomLink>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Container>
    </>
  );
};

export default RealEstateAgency;
